import { ReactNode, useEffect } from 'react';
import { ColumnMeta, RowData } from '@tanstack/react-table';
import classNames from 'classnames';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { WebEDIError } from 'services/webediErrors/types';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { FieldState } from 'components/Form/types';

type InputWrapperCellProps = {
  fieldId: string;
  isHighlighted: boolean;
  children: ReactNode;
  className?: string;
  meta: ColumnMeta<RowData, unknown> | undefined;
  fieldState: FieldState;
};
export const InputWrapperCell = ({
  fieldId,
  isHighlighted,
  children,
  className,
  meta,
  fieldState,
}: InputWrapperCellProps) => {
  const isValidating = useWebEDIStore((state) => state.isValidating);
  const { service, ErrorModalComponent, useErrorModal } = useErrorContext<WebEDIError>();
  const error = service.useErrorById(fieldId);
  const { showErrorModal, setShowErrorModal, floatingStyles, refs } = useErrorModal();
  const numberOfErrors = service.useNumberOfActiveErrors();
  const setCurrentError = service.setCurrentError;

  useEffect(() => {
    if (showErrorModal && error) {
      setCurrentError(error);
    }
  }, [showErrorModal, error, setCurrentError]);

  return (
    <>
      <div
        onFocus={() => setShowErrorModal(true)}
        onBlur={() => setShowErrorModal(false)}
        className={classNames(
          'group flex justify-between gap-2 items-center w-full h-full',
          {
            'bg-red-50 text-red-700 focus-within:text-inherit focus-within:!bg-transparent focus-within:ring-red-500 focus-within:shadow-red-500 hover:bg-red-100':
              fieldState === 'error',
            'bg-blue-50 text-blue-700 focus-within:text-inherit focus-within:!bg-transparent hover:bg-blue-100':
              fieldState === 'pending-validation',
            'hover:bg-procuros-green-50 focus-within:!bg-transparent focus-within:ring-procuros-green-500 focus-within:shadow-procuros-green-500':
              fieldState === 'default',
            'bg-amber-100': isHighlighted,
          },
          className,
        )}
        ref={refs.setReference}
      >
        {children}
      </div>
      {showErrorModal && ErrorModalComponent && (
        <ErrorModalComponent
          error={error}
          viewMode={false}
          ref={refs.setFloating}
          style={floatingStyles}
          numberOfErrors={numberOfErrors}
          isValidating={isValidating}
          isPendingValidation={fieldState === 'pending-validation'}
          fieldDescription={meta?.description}
        />
      )}
    </>
  );
};
